import { useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { DataTable, Details, type RecordData } from './Workflow';
import { EmptyState, ErrorState, LoadingState, PageHeader } from './Ui';
import Dialog from './Dialog';
import { useOptionalUser } from '../auth';
import DateRangePicker from './DateRangePicker';
import { isDay, resolveRange, shortDay as calendarDay, todayIn } from '../date-range';
import useCallHistory from './useCallHistory';
import LsaSummary, { type LsaSummaryData } from './LsaSummary';
import { DISPOSITIONS, dispositionLabel } from '../dispositions';
import FilterPill from './FilterPill';
import { callerLabel, callTimeLabel, holdLabel, leadIdOf, phoneLabel, territoryOptions, type Territory } from '../lsa-call-format';
import CallbackQueue from './CallbackQueue';


export async function callRequest(path: string, token?: string, body?: object, method = body ? 'PUT' : 'GET') {
  const response = await fetch(path, { method, credentials: token ? 'omit' : 'include',
    headers: { 'X-NL-AOS-Request': '1', ...(token ? { 'X-NL-AOS-Portal': token } : {}),
      ...(body ? { 'Content-Type': 'application/json' } : {}) }, body: body ? JSON.stringify(body) : undefined });
  if (!response.ok) {
    if (response.status === 401 && !token) window.dispatchEvent(new Event('nl-aos-session-expired'));
    const error = await response.json().catch(() => null);
    throw Error(error?.error || 'The call could not be loaded. Please try again.');
  }
  return response;
}
function googleLink(value: unknown) {
  try {
    const url = new URL(String(value));
    if (url.origin === 'https://ads.google.com' && !url.username && !url.password &&
      (url.pathname === '/localservices/' || /^\/localservicesads\/attachment\/[A-Za-z0-9_-]+\/?$/.test(url.pathname)))
      return url.origin + url.pathname;
  } catch { /* Invalid provider link. */ }
  return 'https://ads.google.com/localservices/';
}
type ActionsProps = {
  call: RecordData; clientId: number; token?: string; onSaved?: (call: RecordData) => void; canEdit?: boolean;
};
export function LsaCallActions(props: ActionsProps) {
  return <ScopedLsaCallActions key={`${props.clientId}:${props.call.id}:${props.token || ''}`} {...props} />;
}
function ScopedLsaCallActions({ call, clientId, token, onSaved, canEdit = true }: ActionsProps) {
  // Only the agency edits the call center's disposition; a client login or shared link never does.
  const user = useOptionalUser();
  const staff = !token && ['superadmin', 'exec'].includes(String(user?.role || ''));
  const [flag, setFlag] = useState(!!call.office_follow_up), [saving, setSaving] = useState(false),
    [error, setError] = useState(''), [audio, setAudio] = useState(''), [loading, setLoading] = useState(false);
  const alive = useRef(true);
  const audioUrl = useRef('');
  useEffect(() => { setFlag(!!call.office_follow_up); }, [call.office_follow_up]);
  useEffect(() => { alive.current = true; return () => { alive.current = false; if (audioUrl.current) URL.revokeObjectURL(audioUrl.current); }; }, []);
  const path = `/api/lsa/${clientId}/calls/${call.id}`;
  async function save(next: boolean) {
    setSaving(true); setError('');
    try {
      const updated = await (await callRequest(`${path}/follow-up`, token, { office_follow_up: next })).json();
      if (alive.current) { setFlag(updated.office_follow_up); onSaved?.(updated); }
    } catch (e) { if (alive.current) setError((e as Error).message); }
    finally { if (alive.current) setSaving(false); }
  }
  async function listen() {
    setLoading(true); setError('');
    try {
      const response = await callRequest(`${path}/recording`, token);
      const blob = await response.blob();
      if (alive.current) { audioUrl.current = URL.createObjectURL(blob); setAudio(audioUrl.current); }
    } catch (e) { if (alive.current) setError((e as Error).message); }
    finally { if (alive.current) setLoading(false); }
  }
  return <div className="call-review-controls">
    <div className="call-listening">
      {call.recording_available && !audio && <button className="button secondary" disabled={loading} onClick={listen}>{loading ? 'Loading recording…' : 'Listen to call'}</button>}
      {audio && <audio controls autoPlay preload="none" src={audio} aria-label="LSA call recording" onError={() => setError('This recording could not be played. Open it in Google LSA.')} />}
      <a href={googleLink(call.lsa_url)} target="_blank" rel="noopener noreferrer">Open in Google LSA</a>
    </div>
    {!call.recording_available && <p className="workflow-note">No recording is saved for this call. Check the lead in Google LSA.</p>}
    {/* Tick it once someone calls the lead back, so a long list shows who is done (Shawn, 2026-10-30). */}
    <label className="call-follow-up"><input type="checkbox" aria-label="Called back" checked={flag} disabled={saving || !canEdit} onChange={e => save(e.target.checked)} />Called back{saving ? ': saving…' : flag && call.follow_up_updated_at ? ` ${shortDay(call.follow_up_updated_at)}` : ''}</label>
    <p className="workflow-note">Opening a call in Google LSA needs a Google login with access to this Local Services Ads account.</p>
    {canEdit && staff && <LsaDisposition call={call} clientId={clientId} onSaved={onSaved} />}
    {canEdit && <LsaRating call={call} clientId={clientId} token={token} onSaved={onSaved} />}
    <LsaFollowUps call={call} clientId={clientId} token={token} onSaved={onSaved} canEdit={canEdit} />
    {error && <p className="inline-notice danger" role="alert">{error}</p>}
  </div>;
}

/**
 * Correct a disposition the AI read wrong (Shawn, 2026-10-28). Staff only: it re-labels
 * the call, which the booked rate, the call-back list and the rate-in-LSA list read from.
 */
function LsaDisposition({ call, clientId, onSaved }: { call: RecordData; clientId: number; onSaved?: (call: RecordData) => void }) {
  const current = call.disposition ? String(call.disposition) : '';
  const currentNote = call.disposition_note ? String(call.disposition_note) : '';
  const currentAgent = call.agent_name ? String(call.agent_name) : '';
  const [value, setValue] = useState(current), [note, setNote] = useState(currentNote), [agent, setAgent] = useState(currentAgent);
  const [saving, setSaving] = useState(false), [error, setError] = useState(''), [saved, setSaved] = useState(false);
  const dirty = value !== current || note !== currentNote || agent.trim() !== currentAgent;
  async function save() {
    if (!value) return;
    setSaving(true); setError(''); setSaved(false);
    try {
      const updated = await (await callRequest(`/api/lsa/${clientId}/calls/${call.id}/disposition`, undefined, { disposition: value, note, ...(agent.trim() !== currentAgent ? { agent_name: agent.trim() } : {}) })).json();
      onSaved?.(updated); setSaved(true);
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  }
  return <div className="lsa-disposition">
    <label>Disposition
      <select value={value} disabled={saving} onChange={e => { setValue(e.target.value); setSaved(false); }}>
        {!current && <option value="" disabled>Not sorted yet</option>}
        {Object.entries(DISPOSITIONS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </select>
    </label>
    <label>Disposition note<input value={note} maxLength={280} disabled={saving} placeholder="Optional: why this call is this way" onChange={e => { setNote(e.target.value); setSaved(false); }} /></label>
    <label>Agent<input value={agent} maxLength={40} disabled={saving} placeholder="Who answered" onChange={e => { setAgent(e.target.value); setSaved(false); }} /></label>
    <button type="button" className="button secondary" disabled={saving || !value || !dirty} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
    {saved && !dirty && <span className="workflow-note">Saved.</span>}
    {error && <p className="inline-notice danger" role="alert">{error}</p>}
  </div>;
}

/**
 * A call Google should credit (wrong service, wrong area, spam): whoever rates it in
 * LSA, the team or the owner, marks it here so it leaves the "Rate in LSA" list.
 */
function LsaRating({ call, clientId, token, onSaved }: { call: RecordData; clientId: number; token?: string; onSaved?: (call: RecordData) => void }) {
  const [saving, setSaving] = useState(false), [error, setError] = useState('');
  if (!call.needs_lsa_rating && !call.lsa_rated_at) return null;
  async function save(rated: boolean) {
    setSaving(true); setError('');
    try { onSaved?.(await (await callRequest(`/api/lsa/${clientId}/calls/${call.id}/rated`, token, { rated })).json()); }
    catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  }
  return <div className="lsa-rating">
    {call.lsa_rated_at
      ? <p>Rated in LSA {shortDay(call.lsa_rated_at)}. <button type="button" className="text-button" disabled={saving} onClick={() => save(false)}>Undo</button></p>
      : <><p>Google can credit this call. In LSA, open the lead, choose Rate, then Dissatisfied, and pick the reason that matches ({dispositionLabel(call.disposition)?.toLowerCase()}).</p>
        <button type="button" className="button secondary" disabled={saving} onClick={() => save(true)}>{saving ? 'Saving…' : 'Mark as rated in LSA'}</button></>}
    {error && <p className="inline-notice danger" role="alert">{error}</p>}
  </div>;
}

/** A saved moment as a short Eastern day: "Oct 30". */
function shortDay(value: unknown): string {
  const at = new Date(String(value));
  return Number.isNaN(at.getTime()) ? '' : at.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' });
}
/** A follow-up day as a short date, read from "YYYY-MM-DD" without a timezone shift. */
function followUpDate(value: unknown): string {
  const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  if (!day) return '';
  return new Date(`${day[0]}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
/**
 * Secondary call logging (Shawn, 2026-10-30): an owner calls a lead back and turns it
 * around. Each follow-up is logged as its own call linked to the original, shown as a thread
 * underneath it, so the first call keeps the call center's real result. Owners can add these too.
 */
function LsaFollowUps({ call, clientId, token, onSaved, canEdit = true }: ActionsProps) {
  // The list already carries the count, so a call with none loads nothing and reads instantly.
  const hadFollowUps = (Number(call.follow_up_count) || 0) > 0;
  const [items, setItems] = useState<RecordData[] | null>(hadFollowUps ? null : []), [error, setError] = useState('');
  const [adding, setAdding] = useState(false), [saving, setSaving] = useState(false);
  const [disposition, setDisposition] = useState('booked'), [when, setWhen] = useState(todayIn());
  const [who, setWho] = useState(''), [note, setNote] = useState('');
  const [phone, setPhone] = useState(call.caller_phone ? String(call.caller_phone) : '');
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const path = `/api/lsa/${clientId}/calls/${call.id}/follow-ups`;
  const order = (list: RecordData[]) => [...list].sort((a, b) =>
    String(b.occurred_on).localeCompare(String(a.occurred_on)) || Number(b.id) - Number(a.id));
  const pushCount = (list: RecordData[], calledBack = false) => onSaved?.({ ...call, follow_up_count: list.length, follow_up_outcome: list[0]?.disposition ?? null,
    // The server ticks Called back when a follow-up is logged; mirror it without a reload.
    ...(calledBack && !call.office_follow_up ? { office_follow_up: true, follow_up_updated_at: new Date().toISOString() } : {}) });
  useEffect(() => {
    if (!hadFollowUps) return;
    let cancelled = false;
    callRequest(path, token).then(r => r.json()).then(data => { if (!cancelled) setItems(Array.isArray(data) ? data : []); })
      .catch(e => { if (!cancelled) setError((e as Error).message); });
    return () => { cancelled = true; };
  }, [path, token, hadFollowUps]);
  async function add() {
    setSaving(true); setError('');
    try {
      const body = { disposition, occurred_on: when, called_by_name: who.trim() || undefined, caller_phone: phone.trim() || undefined, note: note.trim() || undefined };
      const created = await (await callRequest(path, token, body, 'POST')).json();
      if (!alive.current) return;
      const list = order([created, ...(items || [])]);
      setItems(list); pushCount(list, true); setAdding(false); setWho(''); setNote(''); setDisposition('booked');
    } catch (e) { if (alive.current) setError((e as Error).message); }
    finally { if (alive.current) setSaving(false); }
  }
  async function remove(id: unknown) {
    setError('');
    try {
      await callRequest(`${path}/${id}`, token, undefined, 'DELETE');
      if (!alive.current) return;
      const list = (items || []).filter(f => f.id !== id);
      setItems(list); pushCount(list);
    } catch (e) { if (alive.current) setError((e as Error).message); }
  }
  return <div className="lsa-follow-ups">
    <h4>Follow-ups</h4>
    {items === null ? <p className="workflow-note">Loading follow-ups…</p> :
      items.length === 0 ? <p className="workflow-note">No follow-ups yet. Log one when someone calls this lead back, so the first call keeps the call center's result.</p> :
      <ul className="lsa-follow-up-list">{items.map(f => <li key={String(f.id)}>
        <div><strong>{dispositionLabel(f.disposition)}</strong> <span className="muted">on {followUpDate(f.occurred_on)}</span>
          {f.called_by_name ? <span className="muted"> by {String(f.called_by_name)}</span> : null}
          {f.caller_phone ? <span className="muted"> · {phoneLabel(f.caller_phone)}</span> : null}</div>
        {f.note ? <div className="lsa-follow-up-note">{String(f.note)}</div> : null}
        {canEdit && <button type="button" className="text-button" onClick={() => remove(f.id)}>Remove</button>}
      </li>)}</ul>}
    {canEdit && (adding ?
      <div className="lsa-follow-up-form">
        <label>What happened
          <select value={disposition} disabled={saving} onChange={e => setDisposition(e.target.value)}>
            {Object.entries(DISPOSITIONS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <label>Date<input type="date" value={when} disabled={saving} onChange={e => setWhen(e.target.value)} /></label>
        <label>Who called<input value={who} maxLength={80} disabled={saving} placeholder="Usually the owner" onChange={e => setWho(e.target.value)} /></label>
        <label>Number called<input value={phone} maxLength={40} disabled={saving} placeholder="The lead's number" onChange={e => setPhone(e.target.value)} /></label>
        <label>Note<input value={note} maxLength={280} disabled={saving} placeholder="Optional: what was said" onChange={e => setNote(e.target.value)} /></label>
        <div className="lsa-follow-up-actions">
          <button type="button" className="button secondary" disabled={saving || !when} onClick={add}>{saving ? 'Saving…' : 'Save follow-up'}</button>
          <button type="button" className="text-button" disabled={saving} onClick={() => setAdding(false)}>Cancel</button>
        </div>
      </div> :
      <button type="button" className="button secondary" onClick={() => setAdding(true)}>Log a follow-up</button>)}
    {error && <p className="inline-notice danger" role="alert">{error}</p>}
  </div>;
}

/** One LSA call as the tables and the dialog show it: plain labels, no provider codes ("NEW", "HIGH"). */
const RECOVERY: Record<string, string> = { HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low', NONE: 'None' };
const sentenceCase = (v: unknown) => (v == null || v === '' ? null : String(v).charAt(0).toUpperCase() + String(v).slice(1).toLowerCase().replace(/_/g, ' '));
function callRow(c: RecordData): RecordData {
  // The disposition stays the call center's result; any follow-up turnaround reads beside it.
  const disp = dispositionLabel(c.disposition);
  const follows = Number(c.follow_up_count) || 0;
  const outcome = follows ? dispositionLabel(c.follow_up_outcome) : null;
  return {
    id: c.id, call_date: c.call_date, time: callTimeLabel(c), territory: c.territory, caller: callerLabel(c), lead_id: leadIdOf(c), duration: c.duration_seconds,
    disposition: disp ? (follows ? `${disp} (+${follows} follow-up${follows > 1 ? 's' : ''}${outcome ? `: ${outcome}` : ''})` : disp) : null,
    lead_status: sentenceCase(c.lead_status), office_follow_up: c.office_follow_up ? shortDay(c.follow_up_updated_at) || 'Yes' : <span className="call-not-back">—</span>,
    call_score: c.nepq_score == null ? 'No score' : `${c.nepq_score}/10`,
    recovery_chance: c.recovery_probability == null ? null : RECOVERY[String(c.recovery_probability).toUpperCase()] || sentenceCase(c.recovery_probability),
  };
}
/** The dialog's fields: the row's, without the follow-up flag (the checkbox above it) and the id.
 * The call content (what happened, coaching, buyer signals, objections, transcript) only
 * arrives from the per-call detail endpoint, so it fills in once that loads. */
function detailOf(c: RecordData): RecordData {
  const r = callRow(c);
  // A named caller's number on its own line; an unnamed caller already reads by it.
  const phone = phoneLabel(c.caller_phone);
  // The dialog shows the call center's own disposition; the follow-up thread lists turnarounds.
  const disp = dispositionLabel(c.disposition);
  return { call_date: r.call_date, time: r.time, territory: r.territory, caller: r.caller,
    phone: phone && phone !== r.caller ? phone : null, duration: r.duration,
    agent: c.agent_name || null,
    time_on_hold: holdLabel(c),
    lead_id: r.lead_id,
    disposition: disp ? `${disp}${c.disposition_note ? `. ${c.disposition_note}` : ''}` : null,
    lead_status: r.lead_status, call_score: r.call_score, recovery_chance: r.recovery_chance,
    charged_by_google: typeof c.charged === 'boolean' ? (c.charged ? 'Yes' : 'No') : null,
    what_happened: c.diagnostic_text ?? null,
    coaching_note: c.coaching_note ?? null,
    buyer_signals: Array.isArray(c.buyer_signals) && c.buyer_signals.length ? c.buyer_signals : null,
    objections: Array.isArray(c.objections_detected) && c.objections_detected.length ? c.objections_detected : null,
    transcript: c.transcript ?? null };
}
/** The call's day without this year's year ("Oct 20"), so date and time each fit one line; an older call keeps its year. */
function callDay(value: unknown, year: string) {
  const day = String(value ?? '').slice(0, 10);
  return isDay(day) ? calendarDay(day, year) : '';
}
/** The Show menu, with counts from the summary for the same dates and territory. */
function showOptions(summary: LsaSummaryData | null) {
  const counts = new Map((summary?.dispositions || []).map(d => [d.disposition, d.calls]));
  return [
    { value: '', name: 'All calls', count: summary?.calls },
    { value: 'callback', name: 'Call-back chances', count: summary?.callback_chances },
    { value: 'not_called_back', name: 'Call-back chances not called back', count: summary?.callbacks_open },
    { value: 'called_back', name: 'Called back', count: summary?.called_back },
    { value: 'rate', name: 'To rate in LSA', count: summary?.needs_rating },
    ...Object.entries(DISPOSITIONS).map(([key, name]) => ({ value: `d:${key}`, name, count: counts.get(key) || 0 })),
  ];
}
/** `header`: the portal page's title and line; the call-back list and Refresh then sit on the right of it. */
type CallsProps = { clientId: number; token?: string; header?: { title: string; description: string } };
export default function LsaCalls(props: CallsProps) {
  return <ScopedLsaCalls key={`${props.clientId}:${props.token || ''}`} {...props} />;
}
function ScopedLsaCalls({ clientId, token, header }: CallsProps) {
  const [calls, setCalls] = useState<RecordData[] | null>(null), [error, setError] = useState(''),
    [selected, setSelected] = useState<RecordData | null>(null), [detail, setDetail] = useState<RecordData | null>(null), [tick, setTick] = useState(0);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<LsaSummaryData | null>(null);
  const [queueOpen, setQueueOpen] = useState(false);
  const { page, setPage, allHistory, setAllHistory, range, setRange, show, setShow, query, setQuery } = useCallHistory();
  // One owner can run several LSA territories (Portland, Beaverton, Vancouver): one Google Ads account each.
  const [territories, setTerritories] = useState<Territory[] | null>(null), [territory, setTerritory] = useState('');
  // An agent picked in the close-rate table narrows the list to their calls.
  const [agent, setAgent] = useState('');
  const dates = resolveRange(range, todayIn(), 'today');
  const place: Record<string, string> = territory ? { territory } : {};
  // Show: a disposition ("d:booked") or a work list (call-back chances, called back, rate in LSA), like the agency's.
  const shown: Record<string, string> = show.startsWith('d:') ? { disposition: show.slice(2) } : show ? { show } : {};
  const filters = new URLSearchParams({ page: String(page), page_size: '25', ...(query.trim() ? { q: query.trim() } : {}), ...(allHistory ? {} : { from: dates.from, to: dates.to }), ...place, ...shown, ...(agent ? { agent } : {}) }).toString();
  const summaryFilter = new URLSearchParams({ ...(allHistory ? {} : { from: dates.from, to: dates.to }), ...place }).toString();
  useEffect(() => {
    let cancelled = false;
    callRequest(`/api/lsa/${clientId}/territories`, token).then(r => r.json()).then(data => { if (!cancelled && Array.isArray(data)) setTerritories(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [clientId, token, tick]);
  const manyTerritories = (territories?.length || 0) > 1;
  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    // The summary is a bonus: the calls still show when it fails.
    callRequest(`/api/lsa/${clientId}/summary?audience=client${summaryFilter ? `&${summaryFilter}` : ''}`, token).then(r => r.json()).then(data => { if (!cancelled) setSummary(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [clientId, token, tick, summaryFilter]);
  useEffect(() => {
    let cancelled = false;
    setCalls(null); setError(''); setSelected(null);
    callRequest(`/api/lsa/${clientId}/calls?${filters}`, token).then(r => r.json()).then(data => { if (!cancelled) { setCalls(data.calls); setTotal(data.total); } })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [clientId, token, tick, filters]);
  // The list rows carry no transcript, buyer signals or objections, so the open call
  // pulls its full content from the per-call detail endpoint.
  const selectedId = selected?.id;
  useEffect(() => {
    if (selectedId == null) { setDetail(null); return; }
    let cancelled = false;
    setDetail(null);
    callRequest(`/api/lsa/${clientId}/calls/${selectedId}`, token).then(r => r.json())
      .then(data => { if (!cancelled) setDetail(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [clientId, token, selectedId]);
  useEffect(() => {
    if (calls && page > Math.max(1, Math.ceil(total / 25))) setPage(Math.max(1, Math.ceil(total / 25)));
  }, [calls, page, total]);
  function saved(updated: RecordData) {
    setCalls(rows => rows?.map(row => row.id === updated.id ? updated : row) || null);
    setSelected(current => current?.id === updated.id ? updated : current);
  }
  const year = todayIn().slice(0, 4);
  // The date is the row's button, stretched over the whole row (client.css .call-row-open), so a
  // row opens its call without a button of its own; the time sits under the date and the lead ID
  // under the caller, like the agency's list, so no cell wraps.
  const tableRows = (calls || []).map(c => {
    const r = callRow(c), day = callDay(c.call_date, year), time = r.time ? String(r.time) : '';
    return { ...r,
      call_date: <button type="button" className="call-row-open" aria-label={`Open the call from ${r.caller}${day ? `, ${day}` : ''}${time ? ` at ${time}` : ''}`} onClick={() => setSelected(c)}><span className="call-when-date">{day || '—'}</span>{time && <small className="call-when-time">{time}</small>}</button>,
      caller: <span className="call-caller"><span className={c.caller_name ? undefined : 'caller-ending'}>{r.caller}</span>{r.lead_id && <small className="call-lead-id">Lead {r.lead_id}</small>}</span> };
  });
  const actions = <>{(summary?.callbacks_open || 0) > 0 && <button className="button primary" onClick={() => setQueueOpen(true)}>Work the call-back list ({summary!.callbacks_open})</button>}<button className="button secondary" onClick={() => setTick(n => n + 1)}>Refresh calls</button></>;
  return <>
    {header && <PageHeader title={header.title} description={header.description} actions={actions} />}
    <section className="panel workflow-panel lsa-calls">
    {!header && <div className="toolbar"><h2>Local Services Ads calls</h2><div className="lsa-calls-actions">{actions}</div></div>}
    {/* The date pill and All history are one choice: the one in effect is filled (portal.css .lsa-range). */}
    <div className="toolbar lsa-call-filters"><div className={`lsa-range${allHistory ? ' is-all' : ''}`} role="group" aria-label="Call dates"><DateRangePicker value={range} rollingEnd="today" maxDays={36500} label="Call dates" onChange={setRange} /><button type="button" className="lsa-all-history" aria-pressed={allHistory} onClick={() => setAllHistory(!allHistory)}>All history</button></div>{manyTerritories && <FilterPill label="Territory" value={territory} options={territoryOptions(territories)} onChange={v => { setTerritory(v); setPage(1); }} />}<FilterPill label="Show" value={show} options={showOptions(summary)} onChange={setShow} /><label className="search-control"><input aria-label="Search calls" placeholder="Search caller, number or agent" value={query} maxLength={200} onChange={e => setQuery(e.target.value)} /></label></div>
    {summary && summary.calls > 0 && <LsaSummary data={summary} agent={agent} onAgent={name => { setAgent(current => current.toLowerCase() === name.toLowerCase() ? '' : name); setPage(1); }} />}
    {agent && <p className="lsa-agent-filter">Showing {agent}'s calls. <button type="button" className="text-button" onClick={() => { setAgent(''); setPage(1); }}>Show all calls</button></p>}
    {error ? <ErrorState message={error} onRetry={() => setTick(n => n + 1)} /> : !calls ? <LoadingState label="Loading calls" /> : !calls.length ?
      (show || query.trim()
        ? <EmptyState title="No calls match" description="Try another filter or search, or choose All history." />
        : <EmptyState title={allHistory ? 'No LSA calls yet' : 'No calls in these dates'} description={allHistory ? 'Calls from Local Services Ads show here after your agency scans them.' : 'Choose another date range or All history to see earlier calls.'} />) :
      <div className="calls-table"><DataTable rows={tableRows} columns={['call_date', ...(manyTerritories && !territory ? ['territory'] : []), 'caller', 'disposition', 'duration', 'office_follow_up']} action={() => <ChevronRight className="call-row-chevron" aria-hidden="true" />} /></div>}
    {/* The empty state already says there are no calls; one page needs no pager. */}
    {calls && !error && total > 0 && <div className="show-more" aria-label="Call history pages"><span className="muted">{total > 25 ? `${(page - 1) * 25 + 1} to ${Math.min(page * 25, total)} of ${total} calls` : `${total} ${total === 1 ? 'call' : 'calls'}`}{allHistory ? ', all history' : ''}</span>{total > 25 && <><button className="button secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button><button className="button secondary" disabled={page * 25 >= total} onClick={() => setPage(page + 1)}>Next</button></>}</div>}
    {queueOpen && <CallbackQueue clientId={clientId} token={token} filters={summaryFilter} onClose={() => { setQueueOpen(false); setTick(n => n + 1); }} />}
    {selected && <Dialog title="Call details" onClose={() => setSelected(null)}><LsaCallActions key={selected.id} call={selected} clientId={clientId} token={token} onSaved={saved} /><Details record={detailOf(detail && detail.id === selected.id ? detail : selected)} /></Dialog>}
  </section></>;
}
